import { BasicButton, PrimaryButton } from "@certn/cascadia";
import {
  Container,
  Grid,
  Layout,
  ProgressLoader,
  SecondaryTopNav,
  Stack,
  Text,
} from "@certn/cascadia-react";
import { PropsWithChildren, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useReferenceCheckTemplatesContext } from "./ReferenceCheckTemplatesContext";
import { CancelModal } from "../../check-arguments-templates-shared";

interface Props extends PropsWithChildren {
  disableCompletion?: boolean;
  submitting?: boolean;
  onComplete?: () => void;
}

export function ReferenceCheckTemplatePageLayout({
  disableCompletion,
  submitting,
  onComplete,
  children,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isLastStep, numberOfSteps, currentStep, goToPrevStep } =
    useReferenceCheckTemplatesContext();
  const [showCancelModal, setShowCancelModal] = useState(false);

  const handleCancel = () => {
    setShowCancelModal(false);
    navigate("/settings/reference-check-templates");
  };

  const isFirstStep = currentStep === 0;
  const progressValue =
    numberOfSteps <= 1
      ? 100
      : Math.round(((currentStep + 1) / numberOfSteps) * 100);

  return (
    <Layout
      layout="narrow"
      header={
        <SecondaryTopNav
          testID="close-button"
          leftIcon={isFirstStep ? "close" : "arrow_back"}
          onleftIconOnPress={
            isFirstStep ? () => setShowCancelModal(true) : goToPrevStep
          }
        >
          <BasicButton
            onPress={() => setShowCancelModal(true)}
            isLoading={submitting}
            testID="reference-check-template-cancel-button"
          >
            {t("Cancel")}
          </BasicButton>
          <PrimaryButton
            onPress={() => onComplete?.()}
            isDisabled={disableCompletion}
            isLoading={submitting}
            testID="reference-check-template-primary-button"
          >
            {isLastStep ? t("Save template") : t("Next step")}
          </PrimaryButton>
        </SecondaryTopNav>
      }
    >
      <Container>
        <div>
          <Text style="input/section-title">
            {t("step {{currentStep}} of {{numberOfSteps}}", {
              currentStep: currentStep + 1,
              numberOfSteps,
            })}
          </Text>
          <Stack gap="space-3">
            <Text as="h1" style="global/title-medium">
              {t("Create reference check template")}
            </Text>
            <ProgressLoader size="small" floating value={progressValue} />
          </Stack>
        </div>

        <Grid form>{children}</Grid>

        {showCancelModal && (
          <CancelModal
            isOpen={showCancelModal}
            onConfirm={handleCancel}
            onClose={() => setShowCancelModal(false)}
          />
        )}
      </Container>
    </Layout>
  );
}
